import { useState, useEffect } from 'react';
import {
  FileText,
  Users,
  Clock,
  CheckCircle,
  XCircle,
  Search,
  Filter,
  Download,
  Eye,
  ArrowLeft,
  Calendar,
  DollarSign,
  AlertCircle,
  LogOut,
  Moon,
  Sun,
  Settings,
  Image as ImageIcon
} from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { useNavigate, Link } from 'react-router-dom';
import { useWorkdayClientTheme } from '../../contexts/WorkdayClientThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import WorkdayClientHeader from '../../components/workday-client/WorkdayClientHeader';

interface Contract {
  id: string;
  consultant_id: string;
  job_posting_id: string;
  start_date: string;
  end_date: string;
  rate: number;
  status: string;
  terms: string;
  created_at: string;
}

interface JobPosting {
  id: string;
  job_title: string;
  status: string;
  created_at: string;
  workday_modules: string[];
  employment_type: string;
}

export default function ContractsAndHires() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useWorkdayClientTheme();
  const { user, signOut } = useAuth();
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [jobPostings, setJobPostings] = useState<JobPosting[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'active' | 'pending' | 'completed'>('active');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);

  useEffect(() => {
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      if (!user?.id) return;

      const [contractsResponse, jobsResponse, assignmentsResponse] = await Promise.all([
        supabase
          .from('contracts')
          .select('*')
          .order('created_at', { ascending: false }),
        supabase
          .from('job_postings')
          .select('*')
          .order('created_at', { ascending: false }),
        supabase
          .from('consultant_assignments')
          .select(`
            *,
            consultant_profiles!consultant_assignments_consultant_id_fkey (
              id,
              full_name,
              email,
              phone_number,
              workday_modules,
              hourly_rate
            ),
            companies (
              name
            )
          `)
          .eq('client_id', user.id)
          .order('created_at', { ascending: false })
      ]);

      if (contractsResponse.error) throw contractsResponse.error;
      if (jobsResponse.error) throw jobsResponse.error;
      if (assignmentsResponse.error) throw assignmentsResponse.error;

      setContracts(contractsResponse.data || []);
      setJobPostings(jobsResponse.data || []);
      setAssignments(assignmentsResponse.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredContracts = () => {
    let filtered = contracts;

    switch (activeTab) {
      case 'active':
        filtered = contracts.filter(c => c.status === 'active');
        break;
      case 'pending':
        filtered = contracts.filter(c => c.status === 'pending');
        break;
      case 'completed':
        filtered = contracts.filter(c => c.status === 'completed' || c.status === 'terminated');
        break;
    }

    if (searchTerm) {
      filtered = filtered.filter(c =>
        c.id.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'completed':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'terminated':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-600" />;
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-blue-600" />;
      case 'terminated':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-600" />;
    }
  };

  const stats = {
    active: assignments.filter(a => a.status === 'active').length,
    pending: contracts.filter(c => c.status === 'pending').length,
    completed: assignments.filter(a => a.status === 'completed').length + contracts.filter(c => c.status === 'completed' || c.status === 'terminated').length,
    totalValue: assignments
      .filter(a => a.status === 'active')
      .reduce((sum, a) => sum + (a.hourly_rate || 0), 0)
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${theme === 'dark' ? 'bg-black' : 'bg-gray-50'}`}>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#40E0D0]"></div>
        </div>
      </div>
    );
  }

  const handleLogout = async () => {
    await signOut();
    navigate('/customer/login');
  };

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-black text-white' : 'bg-gray-50 text-gray-900'}`}>
      <WorkdayClientHeader />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-8">
        <button
          onClick={() => navigate('/workday-client/dashboard')}
          className="mb-6 flex items-center text-[#40E0D0] hover:text-[#40E0D0]/80 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Dashboard
        </button>

        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Contracts & Hires</h1>
          <p className={`text-lg ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Manage consultant contracts and track hiring progress
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className={`p-6 rounded-lg ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'} shadow-sm`}>
            <div className="flex items-center justify-between mb-2">
              <h3 className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Active Contracts
              </h3>
              <CheckCircle className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-3xl font-bold">{stats.active}</p>
          </div>

          <div className={`p-6 rounded-lg ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'} shadow-sm`}>
            <div className="flex items-center justify-between mb-2">
              <h3 className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Pending Approval
              </h3>
              <Clock className="w-5 h-5 text-yellow-500" />
            </div>
            <p className="text-3xl font-bold">{stats.pending}</p>
          </div>

          <div className={`p-6 rounded-lg ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'} shadow-sm`}>
            <div className="flex items-center justify-between mb-2">
              <h3 className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Completed
              </h3>
              <FileText className="w-5 h-5 text-blue-500" />
            </div>
            <p className="text-3xl font-bold">{stats.completed}</p>
          </div>

          <div className={`p-6 rounded-lg ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'} shadow-sm`}>
            <div className="flex items-center justify-between mb-2">
              <h3 className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Monthly Value
              </h3>
              <DollarSign className="w-5 h-5 text-[#40E0D0]" />
            </div>
            <p className="text-3xl font-bold">${stats.totalValue.toLocaleString()}</p>
          </div>
        </div>

        <div className={`mb-6 p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'} shadow-sm`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex gap-2">
              <button
                onClick={() => setActiveTab('active')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'active'
                    ? 'bg-[#40E0D0] text-black'
                    : theme === 'dark'
                    ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Active ({stats.active})
              </button>
              <button
                onClick={() => setActiveTab('pending')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'pending'
                    ? 'bg-[#40E0D0] text-black'
                    : theme === 'dark'
                    ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Pending ({stats.pending})
              </button>
              <button
                onClick={() => setActiveTab('completed')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === 'completed'
                    ? 'bg-[#40E0D0] text-black'
                    : theme === 'dark'
                    ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Completed ({stats.completed})
              </button>
            </div>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search contracts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`pl-10 pr-4 py-2 rounded-lg border ${
                  theme === 'dark'
                    ? 'bg-gray-800 border-gray-700 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                } focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent`}
              />
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {/* Active Assignments Section */}
          {activeTab === 'active' && assignments.filter(a => a.status === 'active').length > 0 && (
            <div className="mb-6">
              <h2 className="text-xl font-bold mb-4">Active Consultant Assignments</h2>
              {assignments.filter(a => a.status === 'active').map((assignment) => (
                <div
                  key={assignment.id}
                  className={`p-6 rounded-lg mb-4 ${
                    theme === 'dark' ? 'bg-gray-900 border border-gray-800' : 'bg-white border border-gray-200'
                  } shadow-sm hover:shadow-md transition-shadow`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <h3 className="text-lg font-semibold">
                          {assignment.consultant_profiles?.full_name || 'Consultant'}
                        </h3>
                        <span className="text-xs px-3 py-1 rounded-full border bg-green-100 text-green-800 border-green-200">
                          Active
                        </span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div className="flex items-center gap-2 text-sm">
                          <Users className="w-4 h-4 text-[#40E0D0]" />
                          <span>{assignment.consultant_profiles?.email}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="w-4 h-4 text-[#40E0D0]" />
                          <span>Started: {new Date(assignment.start_date).toLocaleDateString()}</span>
                        </div>
                        {assignment.hourly_rate && (
                          <div className="flex items-center gap-2 text-sm">
                            <DollarSign className="w-4 h-4 text-[#40E0D0]" />
                            <span>${assignment.hourly_rate}/hr</span>
                          </div>
                        )}
                      </div>

                      {assignment.companies && (
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                          Company: {assignment.companies.name}
                        </p>
                      )}

                      {assignment.consultant_profiles?.workday_modules && (
                        <div className="mt-2">
                          <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                            Modules: {Array.isArray(assignment.consultant_profiles.workday_modules)
                              ? assignment.consultant_profiles.workday_modules.join(', ')
                              : assignment.consultant_profiles.workday_modules}
                          </p>
                        </div>
                      )}

                      {assignment.contract_url && (
                        <div className="mt-3">
                          <a
                            href={assignment.contract_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-[#40E0D0] hover:text-[#40E0D0]/80"
                          >
                            <FileText className="w-4 h-4" />
                            View Signed Contract
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {getFilteredContracts().length === 0 && assignments.filter(a => a.status === activeTab).length === 0 ? (
            <div className={`text-center py-12 rounded-lg ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}>
              <FileText className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <h3 className="text-xl font-semibold mb-2">No assignments or contracts found</h3>
              <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                {activeTab === 'active' && 'No active assignments at the moment'}
                {activeTab === 'pending' && 'No pending contracts to review'}
                {activeTab === 'completed' && 'No completed contracts yet'}
              </p>
            </div>
          ) : (
            getFilteredContracts().map((contract) => (
              <div
                key={contract.id}
                className={`p-6 rounded-lg ${
                  theme === 'dark' ? 'bg-gray-900 border border-gray-800' : 'bg-white border border-gray-200'
                } shadow-sm hover:shadow-md transition-shadow`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      {getStatusIcon(contract.status)}
                      <h3 className="text-lg font-semibold">Contract #{contract.id.slice(0, 8)}</h3>
                      <span className={`text-xs px-3 py-1 rounded-full border ${getStatusColor(contract.status)}`}>
                        {contract.status}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar className="w-4 h-4 text-[#40E0D0]" />
                        <span>
                          Start: {new Date(contract.start_date).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar className="w-4 h-4 text-[#40E0D0]" />
                        <span>
                          End: {new Date(contract.end_date).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <DollarSign className="w-4 h-4 text-[#40E0D0]" />
                        <span>${contract.rate}/month</span>
                      </div>
                    </div>

                    {contract.terms && (
                      <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                        {contract.terms}
                      </p>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => setSelectedContract(contract)}
                      className="p-2 rounded-lg border-2 border-[#40E0D0] text-[#40E0D0] hover:bg-[#40E0D0] hover:text-black transition-colors"
                      title="View Details"
                    >
                      <Eye className="w-5 h-5" />
                    </button>
                    <button
                      className="p-2 rounded-lg border-2 border-[#40E0D0] text-[#40E0D0] hover:bg-[#40E0D0] hover:text-black transition-colors"
                      title="Download Contract"
                    >
                      <Download className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {selectedContract && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className={`max-w-2xl w-full rounded-lg p-6 ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold">Contract Details</h2>
                <button
                  onClick={() => setSelectedContract(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Contract ID</label>
                  <p className="text-lg">{selectedContract.id}</p>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">Status</label>
                  <p className="text-lg">
                    <span className={`px-3 py-1 rounded-full text-sm border ${getStatusColor(selectedContract.status)}`}>
                      {selectedContract.status}
                    </span>
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Start Date</label>
                    <p className="text-lg">{new Date(selectedContract.start_date).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">End Date</label>
                    <p className="text-lg">{new Date(selectedContract.end_date).toLocaleDateString()}</p>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-500">Monthly Rate</label>
                  <p className="text-lg font-semibold">${selectedContract.rate?.toLocaleString()}</p>
                </div>

                {selectedContract.terms && (
                  <div>
                    <label className="text-sm font-medium text-gray-500">Terms & Conditions</label>
                    <p className={`text-sm mt-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      {selectedContract.terms}
                    </p>
                  </div>
                )}

                <div className="flex gap-3 mt-6">
                  <button className="flex-1 px-4 py-2 bg-[#40E0D0] text-black rounded-lg hover:bg-[#40E0D0]/90 transition-colors font-medium">
                    Download PDF
                  </button>
                  <button
                    onClick={() => setSelectedContract(null)}
                    className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg hover:bg-gray-100 transition-colors font-medium"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
